import Autocomplete, { type AutocompleteProps } from '@mui/material/Autocomplete';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { forwardRef } from 'react';

import type { OptionSelectionProps } from '../types';

type TagsInputProps = Omit<
  AutocompleteProps<OptionSelectionProps | string, true, boolean, true>,
  'renderInput' | 'options'
> &
  Pick<
    TextFieldProps,
    | 'label'
    | 'error'
    | 'helperText'
    | 'required'
    | 'variant'
    | 'color'
    | 'InputProps'
    | 'inputRef'
    | 'placeholder'
  > & { disablePreventDuplicationAndEmpty?: boolean };

const TagsInput = forwardRef((props: TagsInputProps, ref) => {
  const {
    placeholder,
    helperText,
    error: hasError,
    label,
    required: isRequired,
    variant,
    color,
    InputProps,
    inputRef,
    onChange,
    disablePreventDuplicationAndEmpty: isDisablePreventDuplicationAndEmpty,
    ...restProps
  } = props;

  return (
    <Autocomplete
      multiple
      clearOnBlur
      freeSolo
      disablePortal
      options={[]}
      sx={{ '~.MuiAutocomplete-popper': { display: 'none' } }}
      ref={ref}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={hasError}
          helperText={helperText}
          required={isRequired}
          variant={variant}
          color={color}
          inputRef={inputRef}
          InputProps={{ ...params.InputProps, ...InputProps }}
        />
      )}
      isOptionEqualToValue={() => false}
      filterOptions={(options, params) => {
        const { inputValue } = params;
        const filtered = [];
        if (inputValue !== '') {
          filtered.push({
            label: inputValue,
            value: inputValue,
          });
        }
        return filtered;
      }}
      onChange={(event, values, reason, details) => {
        const formatValue = values.map((item) => item.toString().replace(/ +/g, ' ').trim());
        if (reason === 'createOption' && !isDisablePreventDuplicationAndEmpty) {
          const listOfOldValue = formatValue.slice(0, -1);
          const newValue = formatValue.slice(-1)[0];

          const isEmpty = newValue === '';
          const isDuplicated = listOfOldValue.some((value) => value === newValue);

          onChange?.(
            event,
            isEmpty || isDuplicated ? listOfOldValue : formatValue,
            reason,
            details,
          );
          return;
        }

        onChange?.(event, formatValue, reason, details);
      }}
      {...restProps}
    />
  );
});

TagsInput.displayName = 'TagsInput';

export type { TagsInputProps };
export { TagsInput };
